import { useEffect, useRef } from 'react'

import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { IconOptionsReport, IconOptionsThrash } from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import Report from 'components/UI/Modal/Report/Report'
import { useModalContext } from 'context/modalContext'
import { useOutsideAlerter } from 'helpers/hooks'
import { useGlobalStore } from 'store/global'
import screens from 'styles/screens'

type ChatOptionsMode = 'message' | 'kick'

export interface ChatOptionsDropdownProps {
  isOpen: boolean
  setIsOpen: (_value: boolean) => void
  messageId: string
  role: 'owner' | 'user'
  mode?: ChatOptionsMode
  kickLabel?: string
  kickUserFn?: () => Promise<void> | void
  withoutBottomNavigation?: boolean
  deleteMessageFn?: (_messageId: string) => Promise<void> | void
  reportMessageFn?: (_params: {
    messageId: string
    message: string
    reportType: number
  }) => Promise<void> | void
}

export const ChatOptionsDropdown = ({
  isOpen,
  setIsOpen,
  messageId,
  role,
  mode = 'message',
  kickLabel,
  kickUserFn,
  withoutBottomNavigation = false,
  deleteMessageFn,
  reportMessageFn,
}: ChatOptionsDropdownProps) => {
  const { t } = useTranslation()
  const modalData = useModalContext()
  const optionsRef = useRef<HTMLDivElement>(null)
  const { accountSetupPopup, setAccountSetupPopup } = useGlobalStore()

  useOutsideAlerter(optionsRef, () => {
    setIsOpen(false)
  })

  useEffect(() => {
    if (isOpen) {
      setAccountSetupPopup({ visible: false })
    } else if (accountSetupPopup.shouldShow) {
      setAccountSetupPopup({ visible: true })
    }
  }, [isOpen, accountSetupPopup.shouldShow, setAccountSetupPopup])

  if (!isOpen) return null

  const handleDelete = () => {
    modalData.addModal(
      t('deleteMessage'),
      <ConfirmModal
        body={t('areYouSureYouWantToDeleteThisMessage')}
        confirmFn={async () => {
          try {
            await deleteMessageFn?.(messageId)
            setIsOpen(false)
            modalData.clearModal()
          } catch (err) {
            console.error('[ChatOptionsDropdown] delete failed', err)
          }
        }}
      />
    )
  }

  const handleReport = () => {
    setIsOpen(false)

    modalData.addModal(
      t('reportMessage'),
      <Report
        type='content'
        confirmFn={async (message: string, reportType: number) => {
          try {
            await reportMessageFn?.({
              messageId,
              message,
              reportType,
            })
            modalData.clearModal()
          } catch (err) {
            console.error('[ChatOptionsDropdown] report failed', err)
          }
        }}
        closeFn={() => {
          modalData.clearModal()
        }}
      />
    )
  }

  const handleKick = async () => {
    try {
      await kickUserFn?.()
      setIsOpen(false)
    } catch (err) {
      console.error('[ChatOptionsDropdown] kick failed', err)
    }
  }

  return createPortal(
    <>
      <BackgroundContainer onClick={() => setIsOpen(false)} />
      <DropdownShell>
        <OptionsDropdownWrapper
          ref={optionsRef}
          className='post__options__dropdown post__options__dropdown--active'
          $withoutBottomNavigation={withoutBottomNavigation}
        >
          {mode === 'kick' ? (
            <>
              <div className='separator__line' />

              <div className='post__options__dropdown__section--bot'>
                <div className='post__options__option' onClick={handleKick}>
                  <IconOptionsReport />
                  <span>{kickLabel ?? t('kickUser')}</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className='separator__line' />

              <div className='post__options__dropdown__section--bot'>
                {role === 'owner' ? (
                  <div className='post__options__option' onClick={handleDelete}>
                    <IconOptionsThrash />
                    <span>{t('deleteMessage')}</span>
                  </div>
                ) : (
                  <div className='post__options__option' onClick={handleReport}>
                    <IconOptionsReport />
                    <span>{t('reportMessage')}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </OptionsDropdownWrapper>
      </DropdownShell>
    </>,
    document.body
  )
}

const BackgroundContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99998;
  background: transparent;
`

const DropdownShell = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: flex-end;
`

interface OptionsDropdownWrapperProps {
  $withoutBottomNavigation: boolean
}

const OptionsDropdownWrapper = styled.div<OptionsDropdownWrapperProps>`
  pointer-events: auto;
  width: 100%;
  max-width: 48rem;

  @media (max-width: ${screens.medium}) {
    padding-bottom: 9rem;
  }

  ${(props) =>
    props.$withoutBottomNavigation && `padding-bottom: 2rem !important;`}
`